import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';

const milestones = [
  { year: '2018', title: '大爱仙尊诞生', description: '首章发布，方尘的故事正式开始，引发读者热烈反响。' },
  { year: '2019', title: '突破百万读者', description: '凭借独特的大爱修炼体系，大爱仙尊读者数突破百万，成为当年最热门仙侠作品。' },
  { year: '2020', title: '全平台上线', description: '大爱仙尊在各大阅读平台全面上线，累计阅读量突破十亿次。' },
  { year: '2021', title: 'IP全面开发', description: '启动漫画、动画、游戏等多维度IP开发，大爱仙尊宇宙正式开启。' },
  { year: '2022', title: '海外出版', description: '大爱仙尊翻译出版至日韩、东南亚及欧美市场，中华仙侠文化走向世界。' },
  { year: '2023', title: '动画化启动', description: '大爱仙尊动画正式立项制作，由国内顶级动画团队操刀。' },
  { year: '2024', title: '社区突破五百万', description: '大爱仙尊官方社区注册用户突破五百万，成为最大的仙侠爱好者聚集地。' },
  { year: '2025', title: '大爱仙尊手游上线', description: '同名手游全球公测，首月下载量突破千万，获得App Store年度推荐。' },
  { year: '2026', title: '全新篇章开启', description: '大爱仙尊新篇章连载开启，方尘的传奇仍在继续，更多精彩等你来探索。' },
];

const testimonials = [
  {
    name: '剑心',
    role: '资深读者',
    content: '追了大爱仙尊三年了，从方尘还是炼气期小修士一路看到他成为仙尊。这部作品最打动我的是它传递的大爱理念——不是空洞的说教，而是融入每一个情节中的真挚情感。',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20man%20portrait%20in%20ancient%20style%2C%20clean%20simple%20background%2C%20warm%20lighting%2C%20professional%20headshot&width=100&height=100&seq=avatar-1&orientation=squarish',
  },
  {
    name: '墨韵',
    role: '文学评论人',
    content: '大爱仙尊在仙侠题材中开辟了一条全新道路。它打破了传统仙侠"弱肉强食"的桎梏，提出以大爱之心证道的理念，这是对仙侠文学的一次深刻革新。',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20woman%20portrait%20in%20traditional%20style%2C%20elegant%20and%20scholarly%2C%20clean%20simple%20background%2C%20warm%20lighting%2C%20professional%20headshot&width=100&height=100&seq=avatar-2&orientation=squarish',
  },
  {
    name: '天行健',
    role: '作家协会会员',
    content: '作为同行，我由衷佩服大爱仙尊的创作功力。将修炼体系与情感叙事完美融合，既有热血激昂的战斗场面，又有细腻动人的情感描写，实属难得。',
    avatar: 'https://readdy.ai/api/search-image?query=Middle%20aged%20Chinese%20man%20portrait%2C%20scholarly%20and%20wise%20appearance%2C%20clean%20simple%20background%2C%20warm%20lighting%2C%20professional%20headshot&width=100&height=100&seq=avatar-3&orientation=squarish',
  },
];

export default function AboutPage() {
  const [formUrl] = useState<string>('https://readdy.ai/api/form/d9qa35snlsngrm4llucg');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState<string>('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    if (!formUrl) {
      setFormStatus('error');
      setFormError('表单暂不可用，请稍后重试');
      return;
    }

    const textarea = formData.get('message') as string;
    if (textarea && textarea.length > 500) {
      setFormStatus('error');
      setFormError('留言内容不能超过500个字符');
      return;
    }

    try {
      formData.delete('company_alt');
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (!response.ok || (serverMsg && serverMsg.includes('spam'))) {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Brand Introduction */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading text-foreground-900 font-bold mb-4">
              关于大爱仙尊
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              大爱仙尊是汇聚仙侠世界顶级修炼体系的内容平台。我们以大爱之名，讲述方尘从凡人到仙尊的传奇历程，传播中华仙侠文化精髓，为全球仙侠爱好者打造最纯粹、最热血的内容家园。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold mb-4">
                  品牌故事
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  大爱仙尊诞生于2018年，最初的灵感来源于一个简单的问题——如果修炼不需要冷酷无情，而是以大爱之心证道，会是什么样子？
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  这个想法催生了主角方尘的形象：一个被所有人轻视的废柴少年，却怀揣着最纯粹的善意和对万物的大爱。他不以杀戮证道，不以无情破境，而是以一颗包容天下的心，走出了属于自己的仙尊之路。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  如今，大爱仙尊已经发展成为一个涵盖小说、漫画、动画、游戏等多元内容的IP品牌。但我们始终没有忘记初心——用最好的故事，传递最真挚的大爱。
                </p>
              </div>
              <div className="flex-1 w-full max-w-md">
                <img
                  src="https://readdy.ai/api/search-image?query=Ancient%20Chinese%20scholar%20writing%20calligraphy%20in%20a%20serene%20study%20room%20with%20scrolls%20and%20ink%20stones%2C%20warm%20candlelight%2C%20traditional%20aesthetic%2C%20peaceful%20and%20elegant%20atmosphere%2C%20ink%20wash%20style&width=600&height=450&seq=about-brand-2026&orientation=landscape"
                  alt="大爱仙尊品牌故事"
                  title="大爱仙尊品牌故事"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold text-center mb-10 md:mb-14">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-8 md:space-y-12">
                {milestones.map((item, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="absolute left-4 md:left-1/2 w-2.5 h-2.5 bg-accent-500 rounded-full -translate-x-1/2 mt-1.5 z-10 ring-4 ring-background-50"></div>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'} pl-10 md:pl-0`}>
                      <span className="inline-block text-xs font-medium text-accent-600 bg-accent-50 px-2.5 py-0.5 rounded-full mb-2">
                        {item.year}
                      </span>
                      <h3 className="text-base font-heading text-foreground-900 font-semibold mb-1">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section id="reputation" className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold text-center mb-10 md:mb-14">
              口碑评价
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((item, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-6"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.content}</p>
                  <div className="flex items-center gap-0.5 mt-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-xs text-accent-500"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold text-center mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              有任何问题或建议，欢迎随时与我们联系
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form="true"
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-colors"
                  placeholder="请输入主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                  <span className="text-xs text-foreground-400 ml-2">（不超过500字）</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  required
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-colors resize-none"
                  placeholder="请输入您的留言内容..."
                ></textarea>
              </div>

              <div className="honeypot-field">
                <label htmlFor="company_alt">备用联系方式</label>
                <input
                  type="text"
                  id="company_alt"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                提交留言
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-center text-green-600">感谢您的留言，我们会尽快回复！</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-center text-red-600">{formError}</p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '关于大爱仙尊',
          description: '大爱仙尊品牌介绍、发展历程、口碑评价及联系方式。大爱仙尊，至高仙道，大爱无疆。',
        })}
      </script>
    </>
  );
}